import React from 'react';
import { Checkbox } from 'react-native-paper';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useController } from 'react-hook-form';

import { FormGroupCheckboxProps } from '@nocode/types/formGroup.type';
import { isCanvas } from '@common/utils';

const FormGroupCheckbox = ({
  name,
  options,
  styleConfig,
}: FormGroupCheckboxProps) => {
  const style = createStyles(styleConfig);
  const canvas = isCanvas() && Platform.OS === 'web';

  const {
    field,
    fieldState: { invalid, isTouched, isDirty },
    formState: { touchedFields, dirtyFields },
  } = useController({
    name,
    defaultValue: [],
  });

  const handlePress = (option: FormGroupCheckboxProps['options'][0]) => {
    if (canvas) {
      return;
    }

    if ((field.value ?? []).includes(option.value)) {
      const removeIndex = field.value.findIndex(
        (item: string | number | boolean) => item === option.value
      );

      const copy = field.value.slice();
      copy.splice(removeIndex, 1);
      field.onChange(copy);
    } else {
      field.onChange([...field?.value, option.value]);
    }
  };

  return (
    <View style={style.wrapper}>
      {options.map((option) => (
        <Pressable
          key={option.label}
          style={({ pressed }) => [
            {
              opacity: pressed ? 0.5 : 1,
            },
            style.checkboxItemWrapper,
          ]}
          onPress={() => handlePress(option)}
          disabled={canvas}
        >
          <View style={style.wrapper}>
            <Checkbox.Item
              status={
                (field.value ?? []).includes(option.value)
                  ? 'checked'
                  : 'unchecked'
              }
              label={option.label}
              position="leading"
              color={styleConfig?.activeColor}
              uncheckedColor={styleConfig?.inactiveColor}
              labelStyle={style.label}
            />
          </View>
        </Pressable>
      ))}
    </View>
  );
};

const createStyles = (style: FormGroupCheckboxProps['styleConfig']) => {
  return StyleSheet.create({
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    checkboxItemWrapper: {
      borderRadius: style?.borderRadius || 0,
      borderColor: style?.borderColor,
      borderWidth: style?.borderWidth,
      marginBottom: 8,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: style?.backgroundColor,
      paddingVertical: 0,
    },
    label: {
      color: style?.color,
      textAlign: 'left',
      fontSize: style?.fontSize,
    },
  });
};

export default FormGroupCheckbox;
